/**
 * Website CMS collections: list/create/remove (Super Admin), categories (Super Admin: add, rename, reorder, remove with
 * a move target) and items (editors: EN/AR title and body, category, media, free-form fields, sort, status).
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import type { CmsItemInput } from '@qm/contracts/cms';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, Field, Loadable, Notice, Screen, When } from '../kit/ui';
import type { CmsCategoryView, CmsItemView } from '../api';
import {
  COLLECTION_SLUG_RE, ITEM_FIELD_KEY_RE, canEditCms, canManageCollections, canViewCms, currentWebsiteOrigin, itemsPerCategory, moveItem,
} from '../lib/cms-logic';
import { useAdminApi } from './layout';
import { ClientError, CmsError, ConfirmButton, MediaLibContext, MediaPicker, MediaThumb, useMediaLib } from './cms-shared';

// ================================================================== collections list
export function CmsCollectionsPage() {
  const { t, roles, locale } = useSurface();
  const api = useAdminApi();
  const list = useResource('cms:collections', api.cmsCollections);
  const remove = useAction(api.cmsDeleteCollection);
  if (!canViewCms(roles)) return <Screen title={t('portal.admin.cms.collections.title')}><Notice testId="cms-no-access">{t('portal.admin.cms.noAccess')}</Notice></Screen>;
  const admin = canManageCollections(roles);
  return (
    <Screen title={t('portal.admin.cms.collections.title')} subtitle={t('portal.admin.cms.collections.subtitle')} testId="cms-collections">
      {admin ? <NewCollectionForm onCreated={list.reload} /> : <Notice testId="super-admin-structure">{t('portal.admin.cms.collections.structureNote')}</Notice>}
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(c) => c.key} empty={t('portal.admin.cms.collections.empty')} caption={t('portal.admin.cms.collections.title')} columns={[
          { key: 'k', header: t('portal.admin.cms.collections.key'), render: (c) => <Link to={`/cms/collections/${encodeURIComponent(c.key)}`}><code>{c.key}</code></Link> },
          { key: 'n', header: t('portal.admin.cms.collections.name'), render: (c) => (locale === 'ar' ? c.nameAr : c.nameEn) },
          { key: 'c', header: t('portal.admin.cms.collections.categories'), render: (c) => c.categories },
          { key: 'i', header: t('portal.admin.cms.collections.items'), render: (c) => c.items },
          ...(admin ? [{ key: 'x', header: t('portal.admin.cms.history.actions'), render: (c: { key: string }) => (
            <ConfirmButton testId={`collection-delete-${c.key}`} label={t('portal.admin.remove')} confirmLabel={t('portal.admin.cms.collections.deleteConfirm')}
              question={t('portal.admin.cms.collections.deleteQuestion', { key: c.key })} onConfirm={async () => { if (await remove.run(c.key)) list.reload(); }} disabled={remove.pending} />
          ) }] : []),
        ]} />}
      </Loadable>
      <CmsError error={remove.error} testId="collection-error" />
    </Screen>
  );
}

function NewCollectionForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [key, setKey] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState('');
  const create = useAction(api.cmsCreateCollection);
  const valid = COLLECTION_SLUG_RE.test(key) && nameEn.trim() && nameAr.trim();
  return (
    <Panel data-testid="new-collection">
      <Heading level={2}>{t('portal.admin.cms.collections.new')}</Heading>
      <form className="qx-toolbar" onSubmit={async (e) => {
        e.preventDefault();
        if (valid && (await create.run({ key, nameEn: nameEn.trim(), nameAr: nameAr.trim() }))) { setKey(''); setNameEn(''); setNameAr(''); onCreated(); }
      }}>
        <Field label={t('portal.admin.cms.collections.key')} hint={t('portal.admin.cms.slugHint')}>{(id) => <input id={id} name="key" value={key} maxLength={80} onChange={(e) => setKey(e.target.value.trim().toLowerCase())} />}</Field>
        <Field label={t('portal.admin.cms.nameEn')}>{(id) => <input id={id} name="nameEn" value={nameEn} maxLength={120} onChange={(e) => setNameEn(e.target.value)} />}</Field>
        <Field label={t('portal.admin.cms.nameAr')}>{(id) => <input id={id} name="nameAr" dir="rtl" lang="ar" value={nameAr} maxLength={120} onChange={(e) => setNameAr(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!valid || create.pending}>{t('portal.admin.cms.collections.create')}</Button>
      </form>
      <CmsError error={create.error} />
    </Panel>
  );
}

// ================================================================== one collection
export function CmsCollectionPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const { key = '' } = useParams();
  const cats = useResource(`cms:cats:${key}`, () => api.cmsCategories(key));
  const items = useResource(`cms:items:${key}`, () => api.cmsItems(key));
  const media = useResource('cms:media:', () => api.cmsMedia());
  const origin = currentWebsiteOrigin();
  const lib = useMemo(() => ({ items: media.status === 'ready' ? media.data : [], origin, loading: media.status === 'loading' }), [media.status, media.data, origin]);
  if (!canViewCms(roles)) return <Screen title={t('portal.admin.cms.collections.title')}><Notice testId="cms-no-access">{t('portal.admin.cms.noAccess')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.cms.collections.one', { key })} testId="cms-collection" actions={<Link to="/cms/collections">{t('portal.admin.cms.collections.back')}</Link>}>
      <MediaLibContext.Provider value={lib}>
        <Loadable resource={cats}>
          {(c) => (
            <Loadable resource={items}>
              {(i) => (
                <>
                  <CategoriesPanel key={c.map((x) => `${x.id}:${x.slug}:${x.nameEn}:${x.nameAr}`).join('|')} collection={key} categories={c} items={i} onChanged={() => { cats.reload(); items.reload(); }} />
                  <ItemsPanel collection={key} categories={c} items={i} onChanged={items.reload} />
                </>
              )}
            </Loadable>
          )}
        </Loadable>
      </MediaLibContext.Provider>
    </Screen>
  );
}

export function CategoriesPanel({ collection, categories, items, onChanged }: { collection: string; categories: CmsCategoryView[]; items: CmsItemView[]; onChanged: () => void }) {
  const { t, roles, locale } = useSurface();
  const api = useAdminApi();
  const admin = canManageCollections(roles);
  const [order, setOrder] = useState(() => categories.map((c) => c.id));
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState('none');
  const counts = itemsPerCategory(items);
  const byId = new Map(categories.map((c) => [c.id, c]));
  const rows = order.map((id) => byId.get(id)).filter((c): c is CmsCategoryView => !!c);
  const reorder = useAction(() => api.cmsReorderCategories(collection, order));
  const del = useAction((id: string, target?: string) => api.cmsDeleteCategory(collection, id, target));
  const orderChanged = order.join() !== categories.map((c) => c.id).join();
  const done = async (p: Promise<unknown>) => { if (await p) { setRemoving(null); setEditing(null); onChanged(); } };
  return (
    <Panel data-testid="categories">
      <Heading level={2}>{t('portal.admin.cms.categories.title')}</Heading>
      {!admin ? <Text tone="muted">{t('portal.admin.cms.collections.structureNote')}</Text> : null}
      {rows.length === 0 ? <Text tone="muted">{t('portal.admin.cms.categories.empty')}</Text> : null}
      <ul className="qx-list">
        {rows.map((c, i) => (
          <li key={c.id} data-category={c.id} className="qx-catrow">
            {editing === c.id ? <CategoryForm collection={collection} category={c} onDone={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} /> : (
              <span className="qx-toolbar">
                <code>{c.slug}</code> <span>{locale === 'ar' ? c.nameAr : c.nameEn}</span>
                <Badge>{t('portal.admin.cms.categories.count', { n: counts[c.id] ?? 0 })}</Badge>
                {admin ? (
                  <>
                    <Button onClick={() => setOrder((o) => moveItem(o, i, -1))} disabled={i === 0} aria-label={t('portal.admin.cms.moveUp')}>{t('portal.admin.cms.up')}</Button>
                    <Button onClick={() => setOrder((o) => moveItem(o, i, 1))} disabled={i === rows.length - 1} aria-label={t('portal.admin.cms.moveDown')}>{t('portal.admin.cms.down')}</Button>
                    <Button onClick={() => setEditing(c.id)} data-testid={`category-rename-${c.id}`}>{t('portal.admin.cms.categories.rename')}</Button>
                    <Button data-danger="true" onClick={() => { setRemoving(c.id); setMoveTo('none'); }} data-testid={`category-delete-${c.id}`}>{t('portal.admin.remove')}</Button>
                  </>
                ) : null}
              </span>
            )}
            {admin && removing === c.id ? (
              <div className="qx-confirm" role="group" data-testid="category-remove-prompt">
                {(counts[c.id] ?? 0) > 0 ? (
                  <Field label={t('portal.admin.cms.categories.moveTo', { n: counts[c.id] ?? 0 })}>
                    {(id) => (
                      <select id={id} name="moveTo" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                        <option value="none">{t('portal.admin.cms.categories.uncategorised')}</option>
                        {rows.filter((x) => x.id !== c.id).map((x) => <option key={x.id} value={x.id}>{locale === 'ar' ? x.nameAr : x.nameEn}</option>)}
                      </select>
                    )}
                  </Field>
                ) : <Text>{t('portal.admin.cms.categories.removeEmpty')}</Text>}
                <Button variant="primary" data-danger="true" disabled={del.pending} onClick={() => void done(del.run(c.id, (counts[c.id] ?? 0) > 0 ? moveTo : undefined))}>{t('portal.admin.cms.categories.removeConfirm')}</Button>
                <Button onClick={() => setRemoving(null)}>{t('portal.admin.cms.cancel')}</Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {admin ? (
        <>
          <Button variant="primary" disabled={!orderChanged || reorder.pending} onClick={() => void done(reorder.run())} data-testid="save-order">{t('portal.admin.cms.categories.saveOrder')}</Button>
          <CategoryForm collection={collection} onDone={onChanged} />
        </>
      ) : null}
      <CmsError error={reorder.error ?? del.error} />
    </Panel>
  );
}

function CategoryForm({ collection, category, onDone, onCancel }: { collection: string; category?: CmsCategoryView; onDone: () => void; onCancel?: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [nameEn, setNameEn] = useState(category?.nameEn ?? '');
  const [nameAr, setNameAr] = useState(category?.nameAr ?? '');
  const save = useAction(() => {
    const body = { slug, nameEn: nameEn.trim(), nameAr: nameAr.trim(), sort: category?.sort ?? 0 };
    return category ? api.cmsUpdateCategory(collection, category.id, body) : api.cmsCreateCategory(collection, body);
  });
  const valid = COLLECTION_SLUG_RE.test(slug) && nameEn.trim() && nameAr.trim();
  return (
    <form className="qx-toolbar" data-testid={category ? 'category-edit' : 'category-new'} onSubmit={async (e) => {
      e.preventDefault();
      if (valid && (await save.run())) { if (!category) { setSlug(''); setNameEn(''); setNameAr(''); } onDone(); }
    }}>
      <Field label={t('portal.admin.cms.categories.slug')}>{(id) => <input id={id} name="slug" value={slug} maxLength={80} onChange={(e) => setSlug(e.target.value.trim().toLowerCase())} />}</Field>
      <Field label={t('portal.admin.cms.nameEn')}>{(id) => <input id={id} name="nameEn" value={nameEn} maxLength={120} onChange={(e) => setNameEn(e.target.value)} />}</Field>
      <Field label={t('portal.admin.cms.nameAr')}>{(id) => <input id={id} name="nameAr" dir="rtl" lang="ar" value={nameAr} maxLength={120} onChange={(e) => setNameAr(e.target.value)} />}</Field>
      <Button type="submit" variant="primary" disabled={!valid || save.pending}>{category ? t('portal.admin.save') : t('portal.admin.cms.categories.add')}</Button>
      {onCancel ? <Button onClick={onCancel}>{t('portal.admin.cms.cancel')}</Button> : null}
      <CmsError error={save.error} />
    </form>
  );
}

// ================================================================== items
export function ItemsPanel({ collection, categories, items, onChanged }: { collection: string; categories: CmsCategoryView[]; items: CmsItemView[]; onChanged: () => void }) {
  const { t, roles, locale } = useSurface();
  const editable = canEditCms(roles);
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const catName = (id: string | null) => {
    const c = categories.find((x) => x.id === id);
    return c ? (locale === 'ar' ? c.nameAr : c.nameEn) : t('portal.admin.cms.categories.uncategorised');
  };
  const current = open && open !== 'new' ? items.find((i) => i.id === open) : undefined;
  return (
    <Panel data-testid="items">
      <div className="qx-toolbar">
        <Heading level={2}>{t('portal.admin.cms.items.title')}</Heading>
        {editable ? <Button onClick={() => setOpen('new')} data-testid="item-new">{t('portal.admin.cms.items.new')}</Button> : null}
      </div>
      <DataTable rows={items} rowKey={(i) => i.id} empty={t('portal.admin.cms.items.empty')} onRowClick={(i) => setOpen(i.id)} columns={[
        { key: 's', header: t('portal.admin.cms.items.slug'), render: (i) => <code>{i.slug}</code> },
        { key: 'ti', header: t('portal.admin.cms.items.title'), render: (i) => (locale === 'ar' ? i.titleAr : i.titleEn) },
        { key: 'c', header: t('portal.admin.cms.items.category'), render: (i) => catName(i.categoryId) },
        { key: 'st', header: t('portal.admin.status'), render: (i) => <Badge tone={i.status === 'published' ? 'good' : 'warn'}>{t(`portal.admin.cms.itemStatus.${i.status}`)}</Badge> },
        { key: 'o', header: t('portal.admin.cms.items.sort'), narrow: 'hide', render: (i) => i.sort },
        { key: 'u', header: t('portal.admin.cms.pages.updated'), narrow: 'hide', render: (i) => <When iso={i.updatedAt} /> },
      ]} />
      {open ? (
        <ItemForm key={open} collection={collection} categories={categories} item={current} editable={editable}
          onSaved={() => { setOpen(null); onChanged(); }} onCancel={() => setOpen(null)} />
      ) : null}
    </Panel>
  );
}

const emptyItem = (): CmsItemInput => ({ slug: '', categoryId: null, titleEn: '', titleAr: '', bodyEn: '', bodyAr: '', mediaIds: [], fields: {}, sort: 0, status: 'draft' });

export function ItemForm({ collection, categories, item, editable, onSaved, onCancel }: {
  collection: string; categories: CmsCategoryView[]; item?: CmsItemView; editable: boolean; onSaved: () => void; onCancel: () => void;
}) {
  const { t, locale } = useSurface();
  const api = useAdminApi();
  const lib = useMediaLib();
  const [f, setF] = useState<CmsItemInput>(() => (item ? {
    slug: item.slug, categoryId: item.categoryId, titleEn: item.titleEn, titleAr: item.titleAr, bodyEn: item.bodyEn, bodyAr: item.bodyAr,
    mediaIds: [...item.mediaIds], fields: { ...item.fields }, sort: item.sort, status: item.status,
  } : emptyItem()));
  const [pairs, setPairs] = useState<[string, string][]>(() => Object.entries(item?.fields ?? {}));
  const [picking, setPicking] = useState(false);
  const keysOk = pairs.every(([k]) => ITEM_FIELD_KEY_RE.test(k)) && new Set(pairs.map(([k]) => k)).size === pairs.length;
  const valid = COLLECTION_SLUG_RE.test(f.slug) && f.titleEn.trim() && f.titleAr.trim() && keysOk;
  const body = (): CmsItemInput => ({ ...f, titleEn: f.titleEn.trim(), titleAr: f.titleAr.trim(), fields: Object.fromEntries(pairs) });
  const save = useAction(() => (item ? api.cmsUpdateItem(item.id, body()) : api.cmsCreateItem(collection, body())));
  const del = useAction(() => api.cmsDeleteItem(item!.id));
  const set = <K extends keyof CmsItemInput>(k: K, v: CmsItemInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const dis = !editable;
  return (
    <form className="qx-itemform" data-testid="item-form" onSubmit={async (e) => { e.preventDefault(); if (valid && (await save.run())) onSaved(); }}>
      <Heading level={3}>{item ? t('portal.admin.cms.items.edit', { slug: item.slug }) : t('portal.admin.cms.items.new')}</Heading>
      <div className="qx-grid">
        <Field label={t('portal.admin.cms.items.slug')} hint={t('portal.admin.cms.slugHint')}>{(id) => <input id={id} name="slug" value={f.slug} disabled={dis} maxLength={80} onChange={(e) => set('slug', e.target.value.trim().toLowerCase())} />}</Field>
        <Field label={t('portal.admin.cms.items.category')}>
          {(id) => (
            <select id={id} name="categoryId" value={f.categoryId ?? ''} disabled={dis} onChange={(e) => set('categoryId', e.target.value || null)}>
              <option value="">{t('portal.admin.cms.categories.uncategorised')}</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{locale === 'ar' ? c.nameAr : c.nameEn}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('portal.admin.cms.items.sort')}>{(id) => <input id={id} type="number" name="sort" min={0} max={10000} value={f.sort} disabled={dis} onChange={(e) => set('sort', Math.max(0, Math.min(10000, Math.trunc(Number(e.target.value) || 0))))} />}</Field>
        <Field label={t('portal.admin.status')}>
          {(id) => (
            <select id={id} name="status" value={f.status} disabled={dis} onChange={(e) => set('status', e.target.value as CmsItemInput['status'])}>
              <option value="draft">{t('portal.admin.cms.itemStatus.draft')}</option>
              <option value="published">{t('portal.admin.cms.itemStatus.published')}</option>
            </select>
          )}
        </Field>
      </div>
      <div className="qx-grid">
        <div>
          <Field label={t('portal.admin.cms.items.titleEn')}>{(id) => <input id={id} name="titleEn" value={f.titleEn} maxLength={200} disabled={dis} onChange={(e) => set('titleEn', e.target.value)} />}</Field>
          <Field label={t('portal.admin.cms.items.bodyEn')}>{(id) => <textarea id={id} name="bodyEn" value={f.bodyEn} disabled={dis} onChange={(e) => set('bodyEn', e.target.value)} />}</Field>
        </div>
        <div dir="rtl" lang="ar">
          <Field label={t('portal.admin.cms.items.titleAr')}>{(id) => <input id={id} name="titleAr" value={f.titleAr} maxLength={200} disabled={dis} onChange={(e) => set('titleAr', e.target.value)} />}</Field>
          <Field label={t('portal.admin.cms.items.bodyAr')}>{(id) => <textarea id={id} name="bodyAr" value={f.bodyAr} disabled={dis} onChange={(e) => set('bodyAr', e.target.value)} />}</Field>
        </div>
      </div>

      <fieldset className="qx-group" data-testid="item-media">
        <legend>{t('portal.admin.cms.items.media')}</legend>
        {f.mediaIds.length === 0 ? <Text tone="muted">{t('portal.admin.cms.items.noMedia')}</Text> : null}
        <div className="qx-media-grid">
          {f.mediaIds.map((id, i) => (
            <div key={id} className="qx-media" data-media={id}>
              <MediaThumb media={lib.items.find((m) => m.id === id)} id={id} origin={lib.origin} />
              <code>{id}</code>
              {editable ? (
                <span className="qx-toolbar">
                  <Button onClick={() => set('mediaIds', moveItem(f.mediaIds, i, -1))} disabled={i === 0} aria-label={t('portal.admin.cms.moveUp')}>{t('portal.admin.cms.up')}</Button>
                  <Button onClick={() => set('mediaIds', f.mediaIds.filter((x) => x !== id))}>{t('portal.admin.remove')}</Button>
                </span>
              ) : null}
            </div>
          ))}
        </div>
        {editable ? <Button onClick={() => setPicking((v) => !v)} disabled={f.mediaIds.length >= 30} data-testid="item-media-add">{t('portal.admin.cms.items.addMedia')}</Button> : null}
        {picking ? <MediaPicker selected={f.mediaIds} onClose={() => setPicking(false)}
          onPick={(id) => set('mediaIds', f.mediaIds.includes(id) ? f.mediaIds.filter((x) => x !== id) : [...f.mediaIds, id].slice(0, 30))} /> : null}
      </fieldset>

      <fieldset className="qx-group" data-testid="item-fields">
        <legend>{t('portal.admin.cms.items.fields')}</legend>
        <Text tone="muted">{t('portal.admin.cms.items.fieldsHint')}</Text>
        {pairs.map(([k, v], i) => (
          <div key={i} className="qx-toolbar" data-pair={i}>
            <Field label={t('portal.admin.cms.fields.key')} error={k && !ITEM_FIELD_KEY_RE.test(k) ? t('portal.admin.cms.err.invalid_key') : null}>
              {(id) => <input id={id} value={k} maxLength={40} disabled={dis} onChange={(e) => setPairs((ps) => ps.map((p, j) => (j === i ? [e.target.value.trim(), p[1]] : p)))} />}
            </Field>
            <Field label={t('portal.admin.cms.fields.value')}>{(id) => <input id={id} value={v} maxLength={2000} disabled={dis} onChange={(e) => setPairs((ps) => ps.map((p, j) => (j === i ? [p[0], e.target.value] : p)))} />}</Field>
            {editable ? <Button onClick={() => setPairs((ps) => ps.filter((_, j) => j !== i))}>{t('portal.admin.remove')}</Button> : null}
          </div>
        ))}
        {!keysOk && pairs.length ? <ClientError reason={pairs.some(([k]) => !ITEM_FIELD_KEY_RE.test(k)) ? 'invalid_key' : 'duplicate_key'} /> : null}
        {editable ? <Button onClick={() => setPairs((ps) => [...ps, ['', '']])} data-testid="item-field-add">{t('portal.admin.cms.fields.add')}</Button> : null}
      </fieldset>

      <div className="qx-toolbar">
        {editable ? <Button type="submit" variant="primary" disabled={!valid || save.pending} data-testid="item-save">{t('portal.admin.save')}</Button> : null}
        <Button onClick={onCancel}>{t('portal.kit.close')}</Button>
        {editable && item ? (
          <ConfirmButton testId="item-delete" label={t('portal.admin.cms.items.delete')} confirmLabel={t('portal.admin.cms.items.deleteConfirm')} question={t('portal.admin.cms.items.deleteQuestion')}
            onConfirm={async () => { if (await del.run()) onSaved(); }} disabled={del.pending} />
        ) : null}
      </div>
      <CmsError error={save.error ?? del.error} />
    </form>
  );
}
